import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useElena } from '../state';
import { Icon, type IconName } from './Icon';

interface Command {
  group: string;
  label: string;
  say: string;
  icon: IconName;
}

const commands: Command[] = [
  {
    group: 'Muzika',
    label: 'Pusti Cecu na YouTube-u',
    say: 'Pusti Cecu na YouTube-u',
    icon: 'wave',
  },
  {
    group: 'Muzika',
    label: 'Pusti nešto za igru',
    say: 'Pusti mi neku plejlistu za igru',
    icon: 'wave',
  },
  { group: 'Muzika', label: 'Pauziraj muziku', say: 'Pauziraj muziku', icon: 'stop' },
  { group: 'Muzika', label: 'Sledeća pesma', say: 'Sledeća pesma', icon: 'send' },
  { group: 'Muzika', label: 'Utišaj', say: 'Utišaj zvuk', icon: 'wave' },
  { group: 'FiveM', label: 'Uđi na MILPET', say: 'Upali FiveM i uđi na server', icon: 'zap' },
  { group: 'FiveM', label: 'Samo pokreni FiveM', say: 'Pokreni FiveM', icon: 'zap' },
  {
    group: 'Server',
    label: 'Stanje servera',
    say: 'Kakvo je stanje na serveru i ko je unutra?',
    icon: 'server',
  },
  {
    group: 'Server',
    label: 'Koliko je igrača',
    say: 'Koliko ljudi ima na serveru?',
    icon: 'server',
  },
  { group: 'Računar', label: 'Otvori Discord', say: 'Otvori Discord', icon: 'monitor' },
  { group: 'Računar', label: 'Otvori Steam', say: 'Otvori Steam', icon: 'monitor' },
  { group: 'Računar', label: 'Zaključaj računar', say: 'Zaključaj računar', icon: 'shield' },
  {
    group: 'Kombinacije',
    label: 'Veče za igru',
    say: 'Otvori Discord, upali FiveM i uđi na server, pa pusti muziku za igru',
    icon: 'zap',
  },
];

export function Palette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { send } = useElena();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [routineCommands, setRoutineCommands] = useState<Command[]>([]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [...routineCommands, ...commands];
    const list = q
      ? all.filter((c) => `${c.group} ${c.label} ${c.say}`.toLowerCase().includes(q))
      : all;
    return q
      ? [
          ...list,
          {
            group: 'Pitaj Elenu',
            label: `„${query.trim()}“`,
            say: query.trim(),
            icon: 'chat' as const,
          },
        ]
      : list;
  }, [query, routineCommands]);

  useEffect(() => {
    if (!open) return;
    api
      .routines()
      .then((r) =>
        setRoutineCommands(
          r.routines
            .filter((x) => x.enabled)
            .map((x) => ({
              group: 'Rutine',
              label: x.name,
              say: `Pokreni rutinu ${x.name}`,
              icon: 'zap' as const,
            })),
        ),
      )
      .catch(() => setRoutineCommands([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIndex(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => setIndex(0), [query]);

  if (!open) return null;

  const run = (cmd: Command | undefined) => {
    if (!cmd) return;
    send(cmd.say, false);
    onClose();
  };

  let lastGroup = '';
  return (
    <div
      className="overlay top"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label="Komande">
        <div className="palette-search">
          <Icon name="search" size={20} />
          <label htmlFor="palette-input" className="sr-only">
            Traži komandu
          </label>
          <input
            id="palette-input"
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Šta da uradim?"
            autoComplete="off"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={`cmd-${index}`}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndex((i) => Math.min(i + 1, results.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndex((i) => Math.max(i - 1, 0));
              }
              if (e.key === 'Enter') run(results[index]);
            }}
          />
          <kbd className="kbd">Esc</kbd>
        </div>
        <ul id="palette-list" className="palette-list" role="listbox">
          {results.map((cmd, i) => {
            const header = cmd.group !== lastGroup ? cmd.group : null;
            lastGroup = cmd.group;
            return (
              <li key={`${cmd.group}-${cmd.label}`} role="presentation">
                {header && <div className="palette-group">{header}</div>}
                <button
                  id={`cmd-${i}`}
                  type="button"
                  role="option"
                  aria-selected={i === index}
                  className={`palette-item${i === index ? ' is-active' : ''}`}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => run(cmd)}
                >
                  <Icon name={cmd.icon} />
                  <span className="grow">{cmd.label}</span>
                  {i === index && <kbd className="kbd">↵</kbd>}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="palette-foot muted small">
          <span>
            <kbd className="kbd">↑</kbd> <kbd className="kbd">↓</kbd> izaberi
          </span>
          <span>
            <kbd className="kbd">↵</kbd> izvrši
          </span>
          <span>Bilo šta drugo ide Eleni kao pitanje</span>
        </div>
      </div>
    </div>
  );
}
